"use client";

import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";

import { cn } from "../../lib/utils";
import { VariantProps, cva } from "class-variance-authority";

// Create context for variant
const TabsVariantContext = React.createContext<"default" | "toggle">("default");

const tabsListVariants = cva("flex items-stretch justify-center", {
  variants: {
    variant: {
      default: "gap-1 sm:gap-2 -mb-px",
      toggle: "gap-1 p-1 bg-offwhite border border-muted rounded-md",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const tabsTriggerVariants = cva(
  "inline-flex flex-1 items-center font-semibold justify-center transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 cursor-pointer",
  {
    variants: {
      variant: {
        default:
          "text-sm min-w-20 sm:text-base font-medium text-gray-500 px-2 sm:px-3 py-2 border-b-3 border-transparent data-[state=active]:border-primary data-[state=active]:text-foreground hover:text-foreground flex-1",
        toggle:
          "text-sm text-muted-foreground px-2 sm:px-4 py-1.5 rounded-sm transition-all border border-transparent data-[state=active]:bg-white data-[state=active]:border data-[state=active]:border-input data-[state=active]:text-foreground data-[state=active]:shadow-xs hover:text-foreground flex-1",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

interface TabsProps extends React.ComponentProps<typeof TabsPrimitive.Root> {
  variant?: VariantProps<typeof tabsTriggerVariants>["variant"];
}

export const Tabs = ({
  className,
  variant = "default",
  ...props
}: TabsProps): React.ReactElement => {
  return (
    <TabsVariantContext.Provider value={variant as "default" | "toggle"}>
      <TabsPrimitive.Root
        data-slot="tabs"
        className={cn("flex flex-col gap-2", className)}
        {...props}
      />
    </TabsVariantContext.Provider>
  );
};

interface TabsListProps
  extends React.ComponentProps<typeof TabsPrimitive.List> {
  variant?: VariantProps<typeof tabsListVariants>["variant"];
}

export const TabsList = ({
  className,
  variant,
  ...props
}: TabsListProps): React.ReactElement => {
  // Get variant from context if not explicitly provided
  const contextVariant = React.useContext(TabsVariantContext);
  const finalVariant = variant || contextVariant;

  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(tabsListVariants({ variant: finalVariant }), className)}
      {...props}
    />
  );
};

interface TabsTriggerProps
  extends React.ComponentProps<typeof TabsPrimitive.Trigger> {
  variant?: VariantProps<typeof tabsTriggerVariants>["variant"];
}

export const TabsTrigger = ({
  className,
  variant,
  ...props
}: TabsTriggerProps): React.ReactElement => {
  // Get variant from context if not explicitly provided
  const contextVariant = React.useContext(TabsVariantContext);
  const finalVariant = variant || contextVariant;

  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(tabsTriggerVariants({ variant: finalVariant }), className)}
      {...props}
    />
  );
};

export const TabsContent = ({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>): React.ReactElement => {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("flex-1 outline-none", className)}
      {...props}
    />
  );
};
